import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import {
  constrainRectToAspect,
  cropPixelSize,
  normalizeRect,
  PORTRAIT_9_16,
  type CropRect,
} from './lib/frame-processing';
import { CROP_CURSOR, cropHitTest, moveRect, resizeRect, type CropHandle } from './lib/crop-interaction';
import { containBox } from './lib/skeleton';

/** Grab zone around the box edges, in screen pixels. */
const EDGE_GRAB_PX = 8;

type Gesture =
  | { kind: 'draw'; start: { x: number; y: number } }
  | { kind: 'move'; start: { x: number; y: number }; from: CropRect }
  | { kind: 'resize'; handle: CropHandle; from: CropRect };

interface RemoteCropEditorProps {
  /** The raw shared window (uncropped, unmirrored), so the practitioner sees what they crop. */
  stream: MediaStream;
  initialCrop: CropRect | null;
  onApply: (crop: CropRect) => void;
  onClear: () => void;
  onCancel: () => void;
}

/**
 * In-place crop: drag a box on the live shared window (left), confirm on the right. Drag inside
 * the box to move it, an edge or corner to resize it, outside to draw a new one. The box shows its
 * size in source pixels and is locked to 9:16 portrait by default (switchable).
 */
export default function RemoteCropEditor({ stream, initialCrop, onApply, onClear, onCancel }: RemoteCropEditorProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const gesture = useRef<Gesture | null>(null);
  const [cursor, setCursor] = useState('crosshair');
  const [rect, setRect] = useState<CropRect | null>(initialCrop);
  const [lock916, setLock916] = useState(true);
  const [videoSize, setVideoSize] = useState({ width: 0, height: 0 });
  // Where the shared window's picture actually sits inside the surface (object-contain), in px.
  const [frameBox, setFrameBox] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.srcObject = stream;
    void video.play().catch(() => undefined);
    return () => {
      video.srcObject = null;
    };
  }, [stream]);

  /**
   * The picture's box inside the surface. The video fills the surface with object-contain, so the
   * picture may be letterboxed; every pointer position and the drawn box are relative to this box,
   * never to the surface, so what you drag is exactly what gets cropped.
   */
  const measureFrame = useCallback(() => {
    const surface = surfaceRef.current;
    const video = videoRef.current;
    if (!surface) return null;
    const s = surface.getBoundingClientRect();
    const vw = video?.videoWidth ?? 0;
    const vh = video?.videoHeight ?? 0;
    const box = vw && vh ? containBox(vw, vh, s.width, s.height) : { x: 0, y: 0, width: s.width, height: s.height };
    return { surface: s, box };
  }, []);

  const readVideoSize = useCallback(() => {
    const video = videoRef.current;
    if (video) setVideoSize({ width: video.videoWidth, height: video.videoHeight });
    const m = measureFrame();
    if (m) setFrameBox(m.box);
  }, [measureFrame]);

  // Keep the drawn box on the picture when the pane resizes.
  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => readVideoSize());
    ro.observe(surface);
    return () => ro.disconnect();
  }, [readVideoSize]);

  const shape = useCallback(
    (start: { x: number; y: number }, end: { x: number; y: number }, locked: boolean) =>
      locked
        ? constrainRectToAspect(start, end, videoSize.width, videoSize.height, PORTRAIT_9_16)
        : normalizeRect(start, end),
    [videoSize],
  );

  const toNormalized = (e: React.PointerEvent) => {
    const { surface, box } = measureFrame()!;
    return {
      x: (e.clientX - surface.left - box.x) / box.width,
      y: (e.clientY - surface.top - box.y) / box.height,
    };
  };

  const hitAt = (e: React.PointerEvent) => {
    const { box } = measureFrame()!;
    return cropHitTest(rect, toNormalized(e), EDGE_GRAB_PX / box.width, EDGE_GRAB_PX / box.height);
  };

  const applyGesture = (e: React.PointerEvent) => {
    const g = gesture.current;
    if (!g) return;
    const p = toNormalized(e);
    if (g.kind === 'draw') setRect(shape(g.start, p, lock916));
    else if (g.kind === 'move') setRect(moveRect(g.from, p.x - g.start.x, p.y - g.start.y));
    else setRect(resizeRect(g.from, g.handle, p, lock916, videoSize.width, videoSize.height));
  };

  const toggleLock = (on: boolean) => {
    setLock916(on);
    // Re-shape an existing box from its top-left corner.
    if (on && rect) setRect(shape({ x: rect.x, y: rect.y }, { x: rect.x + rect.w, y: rect.y + rect.h }, true));
  };

  const hasArea = !!rect && rect.w > 0.02 && rect.h > 0.02;
  const size = rect && videoSize.width ? cropPixelSize(rect, videoSize.width, videoSize.height) : null;
  const sizeText = size ? `${size.width} × ${size.height} px` : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row">
      <div className="relative min-h-0 flex-1 overflow-hidden rounded-lg border bg-muted/50">
        {/* The surface fills the pane; the whole shared window is fitted inside it (object-contain). */}
        <div
          ref={surfaceRef}
          data-testid="crop-surface"
          className="absolute inset-0 touch-none select-none"
          style={{ cursor }}
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture?.(e.pointerId);
            const hit = hitAt(e);
            const p = toNormalized(e);
            if (rect && hit === 'move') gesture.current = { kind: 'move', start: p, from: rect };
            else if (rect && hit !== null && hit !== 'move') gesture.current = { kind: 'resize', handle: hit, from: rect };
            else {
              gesture.current = { kind: 'draw', start: p };
              setRect(null);
            }
          }}
          onPointerMove={(e) => {
            if (gesture.current) applyGesture(e);
            else {
              const hit = hitAt(e);
              setCursor(hit ? CROP_CURSOR[hit] : 'crosshair');
            }
          }}
          onPointerUp={(e) => {
            applyGesture(e);
            gesture.current = null;
          }}
        >
          <video
            ref={videoRef}
            muted
            playsInline
            aria-label="Shared call window"
            className="h-full w-full object-contain"
            onLoadedMetadata={readVideoSize}
            onResize={readVideoSize}
          />
          {rect && (
            /* Frame = the visible picture; the crop box is positioned in frame percentages. */
            <div
              data-testid="crop-frame"
              className="pointer-events-none absolute"
              style={
                frameBox
                  ? { left: frameBox.x, top: frameBox.y, width: frameBox.width, height: frameBox.height }
                  : { inset: 0 }
              }
            >
            <div
              className="pointer-events-none absolute border-2 border-primary bg-primary/10"
              style={{ left: `${rect.x * 100}%`, top: `${rect.y * 100}%`, width: `${rect.w * 100}%`, height: `${rect.h * 100}%` }}
            >
              {(['nw', 'ne', 'sw', 'se'] as const).map((corner) => (
                <span
                  key={corner}
                  aria-hidden
                  className={cn(
                    'absolute h-2.5 w-2.5 border border-primary bg-background',
                    corner.includes('n') ? '-top-1.5' : '-bottom-1.5',
                    corner.includes('w') ? '-left-1.5' : '-right-1.5',
                  )}
                />
              ))}
              {sizeText && (
                <span className="absolute left-1 top-1 rounded bg-background/80 px-1.5 py-0.5 text-xs font-medium tabular-nums">
                  {sizeText}
                </span>
              )}
            </div>
            </div>
          )}
        </div>
      </div>
      <Card className="shrink-0 lg:w-80">
        <CardContent className="space-y-3 px-4 py-4">
          <p className="text-sm font-medium">Crop to the client</p>
          <p className="text-sm text-muted-foreground">
            Drag a box around the client on the shared window. Drag inside it to move it, or an edge
            or corner to resize it. Only that area is analysed and captured.
          </p>
          <div className="flex items-center justify-between">
            <Label htmlFor="crop-lock-916">Lock 9:16 (portrait)</Label>
            <Switch id="crop-lock-916" aria-label="Lock 9:16" checked={lock916} onCheckedChange={toggleLock} />
          </div>
          <p className="text-sm tabular-nums" data-testid="crop-size">
            {sizeText ? `Crop: ${sizeText}` : 'Crop: drag a box'}
          </p>
          <Button className="w-full" disabled={!hasArea} onClick={() => rect && onApply(rect)}>
            Apply crop
          </Button>
          {initialCrop && (
            <Button variant="outline" className="w-full" onClick={onClear}>
              Clear crop
            </Button>
          )}
          <Button variant="ghost" className="w-full" onClick={onCancel}>
            Cancel
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
